import {UserModel} from 'app/modules/auth'
import {NotFound} from 'app/modules/errors/NotFound'
import {canOpenAiDataImport} from 'app/pages/school/DataImport/AiImport/utils/access'
import GenerateVoucherRunDriver from 'app/pages/school/GenerateVoucher/components/RunDriver'
import OnboardingWizard from 'app/pages/school/dashboard/OnboardingWizard'
import SchoolPage from 'app/pages/school/school/SchoolPage'
import SchoolToInstitute from 'app/pages/school/school/SchoolToInstitute'
import {
  // getTenantName,
  NavigateWithSearch,
  hasPermission,
  isAdmin,
  isDemocollege,
  isDesignToolEnabled,
  isDevEnv,
  isM_Layout,
  isSelectiveAiDomains,
  isTeacher,
} from 'common/HelperFunctions'
import Spinner from 'common/components/Spinner'
import SuspensedView from 'common/components/SuspensedView'
import {ATTENDANCE_URL_FOR_BRANCH_SELECTION} from 'common/constants'
import {isCurriculumAssistantRouteOn} from 'app/pages/curriculumAssistant/utils/access'
import {lazyWithRetry} from 'common/helpers/lazyWithRetry'
import {isOnboardingCompleteFromPreferences} from 'common/helpers/onboardingCompletion'
import {useIsNewExamManagerEnabled} from 'common/hooks/useIsNewExamManagerEnabled'
import IframeLayout from 'common/layout/IframeLayout'
import WizardLayout from 'common/layout/WizardLayout'
import {usePostHog} from 'posthog-js/react'
import {useEffect} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Navigate, Outlet, Route, Routes} from 'react-router'
import {RootState} from 'setup'
import {getUserPreferences} from 'setup/redux/Actions/Dashboard/dashboard'
import {identifyClarityUser} from 'telemetry/clarity'

import {MasterLayout} from '_metronic/layout/MasterLayout'

async function loadModuleSafe(module: string) {
  try {
    const {default: Module} = await import(`../pages/school/${module}/index.tsx`)
    return Module
  } catch {
    console.warn('Module not available:', module)
    return {}
  }
}

/*
 *     A
 *    A A
 *   AAAAA
 *  A     A
 * A       A
 */
const AccountTypePage = lazyWithRetry(() => import('app/pages/school/AccountType/AccountTypePage'))
const AdmissionFormPage = lazyWithRetry(
  () => import('app/pages/school/AdmissionForm/AdmissionFormPage')
)
const AdmissionVoucherPage = lazyWithRetry(
  () => import('app/pages/school/AdmissionVoucher/AdmissionVoucherPage')
)
const GenerateVoucherPage = lazyWithRetry(
  () => import('app/pages/school/GenerateVoucher/GenerateVoucherPage')
)
const ApplicantFormPage = lazyWithRetry(
  () => import('app/pages/school/AdmissionForm/components/ApplicantForm/ApplicantFormPage')
)
const FeeVoucherPrintPage = lazyWithRetry(
  () => import('app/pages/school/FeeVoucherPrint/FeeVoucherPrintPage')
)
const AnnualCalendarUploadPage = lazyWithRetry(
  () => import('app/pages/school/DataImport/Schedules/AnnualCalendarUploadPage')
)
const AttendancePage = lazyWithRetry(() => import('app/pages/school/Attendance/AttendancePage'))
const AttendanceRemarksPage = lazyWithRetry(
  () => import('app/pages/school/AttendanceRemarks/attendanceRemarksPage')
)
const TeacherStaffAttendanceReportPage = lazyWithRetry(
  () => import('app/pages/school/Attendance/TeacherStaffAttendanceReport')
)
const TeacherSubjectAttendanceReportPage = lazyWithRetry(
  () => import('app/pages/school/Attendance/TeacherSubjectAttendanceReport')
)
const AttendanceSettingPage = lazyWithRetry(
  () => import('app/pages/school/Attendance/AttendanceSettingPage')
)
const AttendanceUploadPage = lazyWithRetry(
  () => import('app/pages/school/Attendance/Upload/AttendanceUploadPage')
)

/*
 * BBBBB
 * B    B
 * BBBBB
 * B    B
 * BBBBB
 */
const BiometricCsvLog = lazyWithRetry(
  () => import('app/pages/school/BiometricCsvLog/BiometricCsvLogPage')
)
const BiometricDeviceLogPage = lazyWithRetry(
  () => import('app/pages/school/BiometricDeviceLog/BiometricDeviceLogPage')
)
const BiometricLogPage = lazyWithRetry(
  () => import('app/pages/school/BiometricLog/BiometricLogPage')
)
const BranchPage = lazyWithRetry(() => import('app/pages/school/Branch/BranchPage'))
const BranchUtilizationReport = lazyWithRetry(
  () => import('app/pages/school/BranchUtilizationReport')
)

/*
 * CCCCC
 * C
 * C
 * C
 * CCCCC
 */
const CertificatePage = lazyWithRetry(() => import('app/pages/school/certificate/CertificatePage'))
const ChatPage = lazyWithRetry(() => import('common/components/chat/ChatPage'))
const ClassPage = lazyWithRetry(() => import('app/pages/school/Class/ClassPage'))
const ClientSecretsPage = lazyWithRetry(
  () => import('app/pages/school/ClientSecrets/ClientSecretsPage')
)
const ContactSupportPage = lazyWithRetry(
  () => import('app/pages/school/ContactSupport/ContactSupportPage')
)
const CstUploadPage = lazyWithRetry(
  () => import('app/pages/school/DataImport/ClassSubjectTeacher/CstUploadPage')
)
const CustomNamesPage = lazyWithRetry(() => import('app/pages/school/CustomNames/CustomNamesPage'))
const ChangeLogPage = lazyWithRetry(() => import('app/pages/school/ChangeLog/ChangeLogPage'))

/*
 * DDDDD
 * D    D
 * D     D
 * D    D
 * DDDDD
 */
const Dashboard = lazyWithRetry(() => import('app/pages/school/dashboard/Dashboard'))
const DatabaseLogPage = lazyWithRetry(() => import('app/pages/school/DatabaseLog/DatabaseLogPage'))
const DepartmentPage = lazyWithRetry(() => import('app/pages/school/department/DepartmentPage'))
const DesignerTool = lazyWithRetry(() => import('app/pages/school/DesignTool/DesignerTool'))
const VersionsHistory = lazyWithRetry(
  () => import('app/pages/school/DesignTool/pages/VersionsHistory')
)
const DigitalRequestPage = lazyWithRetry(
  () => import('app/pages/school/digital-request/DigitalRequestPage')
)
const DiagnosePage = lazyWithRetry(() => import('app/pages/school/Diagnose/DiagnosePage'))

/*
 * EEEEEE
 * E
 * EEEEEE
 * E
 * EEEEEE
 */

const EventPage = lazyWithRetry(() => import('app/pages/school/Event/EventPage'))
const ExamPage = lazyWithRetry(() => import('app/pages/school/Exam/ExamPage'))
const ExamTypePage = lazyWithRetry(() => import('app/pages/school/ExamType/ExamTypePage'))
const ExamTypeLabelsPage = lazyWithRetry(
  () => import('app/pages/school/ExamTypeLabels/ExamTypeLabelsPage')
)
const GradewiseComments = lazyWithRetry(
  () => import('app/pages/school/Exam/components/GradingKey/GradewiseComments')
)
const ExamSchedulePage = lazyWithRetry(
  () => import('app/pages/school/ExamSchedule/ExamSchedulePage')
)
const EmailSettingsPage = lazyWithRetry(
  () => import('app/pages/school/EmailSettings/EmailSettingsPage')
)
const ExamReportCardWordTemplatePage = lazyWithRetry(
  () => import('app/pages/school/ExamReportCardWordTemplateManager/ExamReportCardWordTemplatePage')
)

/*
 * FFFFF
 * F
 * FFF
 * F
 * F
 */
const FileDownloadPage = lazyWithRetry(
  () => import('app/pages/school/FileDownload/FileDownloadPage')
)
const FileTypePage = lazyWithRetry(() => import('app/pages/school/FileType/FileTypePage'))
const FormPage = lazyWithRetry(() => import('app/pages/school/Forms/FormPage'))

/*
 * GGGGG
 * G
 * G  GGG
 * G    G
 * GGGGG
 */
const GradePage = lazyWithRetry(() => import('app/pages/school/Grade/GradePage'))

/*
 * H    H
 * H    H
 * HHHHHH
 * H    H
 * H    H
 */
const HomeworkPage = lazyWithRetry(() => import('app/pages/school/Homework/HomeworkPage'))

/*
 * IIIII
 *   I
 *   I
 *   I
 * IIIII
 */
const InactiveUserPage = lazyWithRetry(
  () => import('app/pages/school/InactiveUser/InactiveUserPage')
)
const InventoryPage = lazyWithRetry(() => import('app/pages/school/Inventory/InventoryPage'))
const InquiryPage = lazyWithRetry(() => import('app/pages/school/Inquiry/InquiryPage'))
const InquirySettingPage = lazyWithRetry(
  () => import('app/pages/school/InquirySetting/InquirySettingPage')
)

/*
 * L
 * L
 * L
 * L
 * LLLLL
 */
const LabelsPage = lazyWithRetry(() => import('app/pages/school/Labels/LabelsPage'))
const LabelsUploadPage = lazyWithRetry(
  () => import('app/pages/school/DataImport/UserLabels/LabelsUploadPage')
)
const LecturePage = lazyWithRetry(() => import('app/pages/school/Lecture/LecturePage'))
const LevelPage = lazyWithRetry(() => import('app/pages/school/level/LevelPage'))
const LibraryPage = await loadModuleSafe('library')

/*
 * M         M
 * MM       MM
 * M M     M M
 * M  M   M  M
 * M   M M   M
 * M    M    M
 */
const MonthlyAuditSummaryPage = lazyWithRetry(
  () => import('app/pages/monthlyAuditSummary/MonthlyAuditSummaryPage')
)

/*
 * N    N
 * NN   N
 * N N  N
 * N  N N
 * N   NN
 * N    N
 */
const NotificationLogPage = lazyWithRetry(
  () => import('app/pages/school/NotificationLog/NotificationLogPage')
)
const NewsPage = lazyWithRetry(() => import('app/pages/school/News/NewsPage'))
const NotificationSettingsPage = lazyWithRetry(
  () => import('app/pages/school/NotificationSettings/NotificationSettingsPage')
)

/*
 *  OOOO
 * O    O
 * O    O
 * O    O
 * O    O
 *  OOOO
 */

const ObservationPage = lazyWithRetry(() => import('app/pages/school/Observations/ObservationPage'))

/*
 * PPPPPPP
 * P     P
 * PPPPPP
 * P
 * P
 */
const ParentPage = lazyWithRetry(() => import('app/pages/school/Parent/ParentPage'))
const PerformanceReportsPage = lazyWithRetry(
  () => import('app/pages/school/PerformanceReports/PerformanceReportsPage')
)
const ProfilePage = lazyWithRetry(() => import('app/pages/common/profile/ProfilePage'))
const KenzerLaunchPage = lazyWithRetry(
  () => import('app/pages/school/Integrations/KenzerLaunchPage')
)
const UserManualEntry = lazyWithRetry(() => import('app/pages/userManual/UserManualEntry'))
const CurriculumAssistantPage = lazyWithRetry(
  () => import('app/pages/curriculumAssistant/CurriculumAssistantPage')
)

/*
 *  QQQQQQ
 * Q      Q
 * Q      Q
 * Q      Q
 * Q      Q
 *  QQQQQQ Q
 *
 */

const QuestionPage = lazyWithRetry(() => import('app/pages/school/Question/QuestionsPage'))

/*
 * RRRRR
 * R    R
 * RRRRR
 * R  R
 * R   R
 */
const ResourcePage = lazyWithRetry(() => import('app/pages/school/resource/ResourcePage'))
const ResourceCategoryPage = lazyWithRetry(
  () => import('app/pages/school/ResourceCategory/ResourceCategoryPage')
)
const ResourceArchiveFolderPage = lazyWithRetry(
  () => import('app/pages/school/ResourceArchiveFolder/ResourceArchiveFolderPage')
)
const ResourceSettingsPage = lazyWithRetry(
  () => import('app/pages/school/ResourceSetting/ResourceSettingPage')
)
const RequestPage = lazyWithRetry(() => import('app/pages/school/request/RequestPage'))
const RoleModulePermissionsPage = lazyWithRetry(
  () => import('app/pages/school/RoleModulePermissions/RoleModulePermissionsPage')
)
const RoyaltyPage = lazyWithRetry(() => import('app/pages/school/Royalty/RoyaltyPage'))

/*
 *  SSSSS
 * S
 *  SSSSS
 *       S
 * SSSSS
 */
const SystemLogPage = lazyWithRetry(() => import('app/pages/school/SystemLog/SystemLogPage'))
const SchedulePage = lazyWithRetry(() => import('app/pages/school/Schedule/SchedulePage'))
const SectionPage = lazyWithRetry(() => import('app/pages/school/Section/SectionPage'))
const SettingsPage = lazyWithRetry(() => import('app/pages/school/settings/GeneralSettingsPage'))
const SmsLogPage = lazyWithRetry(() => import('app/pages/school/SmsLog/SmsLogPage'))
const StaffPage = lazyWithRetry(() => import('app/pages/school/Staff/StaffPage'))
const StaffRolePage = lazyWithRetry(() => import('app/pages/school/StaffRole/StaffRolePage'))
const SyllabusPage = lazyWithRetry(() => import('app/pages/school/Syllabus/SyllabusPage'))
const SupportTicketPage = lazyWithRetry(
  () => import('app/pages/school/TicktingFunctnality/SupportTicketPage')
)

const StoreItemsUploadPage = lazyWithRetry(
  () => import('app/pages/school/DataImport/StoreItems/StoreItemsUploadPage')
)
const StudentAttendanceReport = lazyWithRetry(
  () => import('app/pages/school/Attendance/StudentAttendanceReport')
)
const StudentPage = lazyWithRetry(() => import('app/pages/school/Student/StudentPage'))
const StudentExamUploadPage = lazyWithRetry(
  () => import('app/pages/school/DataImport/Academics/StudentExam/StudentExamUploadPage')
)
const StudentHomeworkUploadPage = lazyWithRetry(
  () => import('app/pages/school/DataImport/Academics/StudentHomework/StudentHomeworkUploadPage')
)
const StudentParentUploadPage = lazyWithRetry(
  () => import('app/pages/school/DataImport/StudentParent/StudentParentUploadPage')
)
const StudentTestUploadPage = lazyWithRetry(
  () => import('app/pages/school/DataImport/Academics/StudentTest/StudentTestUploadPage')
)
const CsvImportPage = lazyWithRetry(
  () => import('app/pages/school/DataImport/CsvImport/CsvImportPage')
)
const AiImportPage = lazyWithRetry(
  () => import('app/pages/school/DataImport/AiImport/AiImportPage')
)
const StudentSubjectAttendanceReport = lazyWithRetry(
  () => import('app/pages/school/Attendance/StudentSubjectAttendanceReport')
)
const StudentSubjectCombinedAttendanceReport = lazyWithRetry(
  () =>
    import('app/pages/school/Attendance/components/utils/StudentSubjectCombinedAttendanceReport')
)
const SubjectsPage = lazyWithRetry(() => import('app/pages/school/Subject/SubjectPage'))
const SubjectGroupPage = lazyWithRetry(
  () => import('app/pages/school/SubjectGroup/SubjectGroupPage')
)
const SportsRecordPage = lazyWithRetry(
  () => import('app/pages/school/SportsRecord/SportsRecordPage')
)
const CounsellingAdminPage = lazyWithRetry(
  () => import('app/pages/school/CollegeCounselling/CollegeCounsellingPage')
)
const StudentCommentsPage = lazyWithRetry(
  () => import('app/pages/school/StudentComments/StudentCommentsPage')
)

/*
 * TTTTTTT
 *    T
 *    T
 *    T
 *    T
 */
const TagsCategoryPage = lazyWithRetry(
  () => import('app/pages/school/tagsCategory/TagsCategoryPage')
)
const TagsPage = lazyWithRetry(() => import('app/pages/school/tags/TagsPage'))
const TagsUploadPage = lazyWithRetry(
  () => import('app/pages/school/DataImport/Tags/TagsUploadPage')
)
const TeacherPage = lazyWithRetry(() => import('app/pages/school/Teacher/TeacherPage'))
const AssignedStudentsPage = lazyWithRetry(
  () => import('app/pages/school/Teacher/AssignedStudentsPage')
)
const TestPage = lazyWithRetry(() => import('app/pages/school/Test/TestPage'))

/*
 * U    U
 * U    U
 * U    U
 * U    U
 *  UUUU
 */
const UserCustomAttendanceSchedulePage = lazyWithRetry(
  () =>
    import('app/pages/school/Attendance/UserCustomAttendanceSchedule/UserCustomAttendanceSchedulePage')
)
const UserManagementPage = lazyWithRetry(
  () => import('app/pages/school/UserManagement/UserManagementPage')
)

/*
 * V    V
 * V    V
 * V    V
 *  V  V
 *   VV
 */
const VendorPage = lazyWithRetry(() => import('app/pages/school/vendor/VendorPage'))

/*
 * W       W
 * W       W
 * W       W
 * W   W   W
 *  W W W W
 *   W   W
 */
const WhatsappLogPage = lazyWithRetry(() => import('app/pages/school/WhatsappLog/WhatsappLogPage'))

const OnboardingWizardRouteGuard = () => {
  const dispatch = useDispatch()

  const userPreferences = useSelector<RootState>(
    ({mainReducer}) => mainReducer.userPreferences,
    shallowEqual
  ) as {[key: string]: any} | null

  const generalSettings = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings,
    shallowEqual
  ) as any

  const user = useSelector<RootState>(({userReducer}) => userReducer.user, shallowEqual) as any

  const legacyOnboardingComplete =
    localStorage.getItem('onboarding_complete') === 'true' ||
    generalSettings?.onboarding_complete === '1' ||
    generalSettings?.onboarding_complete === 1

  // Onboarding should only be shown for user id 1 (primary admin)
  const isOnboardingEligibleUser = Number(user?.id) === 1

  const onboardingPrefsLoaded = userPreferences !== null || legacyOnboardingComplete

  const onboardingCompleteFromPrefs = isOnboardingCompleteFromPreferences(userPreferences)
  const onboardingCompleteFinal =
    onboardingCompleteFromPrefs || legacyOnboardingComplete || !isOnboardingEligibleUser

  useEffect(() => {
    if (legacyOnboardingComplete) return

    // Always fetch fresh preferences (do not skip if cached)
    dispatch(
      getUserPreferences(
        () => {},
        () => {}
      ) as any
    )
  }, [dispatch, legacyOnboardingComplete])

  // Onboarding renders outside MasterLayout, so getGeneralSettings() (which sets document.title
  // via the reducer) never runs here. On a hard reload to /onboarding — e.g. the redirect after
  // clearing sample data — the tab would show the blank index.html title until OnboardingWizard
  // mounts and names it. That mount is gated on preferences loading below, so set a base title
  // immediately to avoid the "missing title" window.
  useEffect(() => {
    const rawTabTitle = generalSettings?.tabTitle
    const tabTitle = typeof rawTabTitle === 'string' ? rawTabTitle.trim() : ''
    document.title = isSelectiveAiDomains() ? 'Da1Timetable' : tabTitle || 'Ilmversity'
  }, [generalSettings])

  // When onboarding is completed, prevent accessing /onboarding by redirecting to dashboard.
  if (onboardingCompleteFinal) return <Navigate to='/dashboard' replace />

  if (!onboardingPrefsLoaded) return null

  return (
    <WizardLayout>
      <OnboardingWizard />
    </WizardLayout>
  )
}

const SelectiveAiOnboardingLayoutGuard = () => {
  const dispatch = useDispatch()

  const userPreferences = useSelector<RootState>(
    ({mainReducer}) => mainReducer.userPreferences,
    shallowEqual
  ) as {[key: string]: any} | null

  const generalSettings = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings,
    shallowEqual
  ) as any

  const user = useSelector<RootState>(({userReducer}) => userReducer.user, shallowEqual) as any

  const legacyOnboardingComplete =
    localStorage.getItem('onboarding_complete') === 'true' ||
    generalSettings?.onboarding_complete === '1' ||
    generalSettings?.onboarding_complete === 1

  // Onboarding should only be shown for user id 1 (primary admin)
  const isOnboardingEligibleUser = Number(user?.id) === 1

  const isSelectiveAiWithPermission = isSelectiveAiDomains() && hasPermission('ai_timetable')
  const shouldGate = isSelectiveAiWithPermission && isOnboardingEligibleUser

  useEffect(() => {
    if (!shouldGate) return
    if (legacyOnboardingComplete) return

    // Always fetch fresh preferences (do not skip if cached)
    dispatch(
      getUserPreferences(
        () => {},
        () => {}
      ) as any
    )
  }, [dispatch, legacyOnboardingComplete, shouldGate])

  if (!shouldGate) return <Outlet />

  const onboardingPrefsLoaded = userPreferences !== null || legacyOnboardingComplete

  const onboardingCompleteFromPrefs = isOnboardingCompleteFromPreferences(userPreferences)
  const onboardingCompleteFinal = onboardingCompleteFromPrefs || legacyOnboardingComplete

  if (!onboardingPrefsLoaded) {
    return (
      <div
        className='d-flex flex-column align-items-center justify-content-center'
        style={{minHeight: '60vh'}}
      >
        <Spinner size='lg' color='primary' />
      </div>
    )
  }

  if (!onboardingCompleteFinal) return <Navigate to='/onboarding' replace />

  return <Outlet />
}

const SchoolRoutes = () => {
  const userBranches = useSelector<RootState>(({mainReducer}) => mainReducer.userBranches) as any
  const posthog = usePostHog()
  const user = useSelector<RootState>((state) => state.userReducer.user) as UserModel
  const userType = useSelector<RootState>((state) => state.userReducer.userType) as string
  const tenant = useSelector<RootState>((state) => state.userReducer.tenant) as string

  /* The exam-module routes that sit outside `exam/*`. The hook, not the plain
     helper: a route table renders before GET /general-settings resolves on a
     prod hard-load, and the stale read would leave the iframe owning them. */
  const newExamManagerEnabled = useIsNewExamManagerEnabled()

  // Kenzer SSO link/route: set by the school admin on My Profile >
  // Integrations and delivered with general settings on every load.
  const kenzerSsoEnabled = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings?.kenzer_roots_sso === 1
  ) as boolean

  let Layout = MasterLayout
  if (isM_Layout()) {
    Layout = IframeLayout
  }

  const permittedBranches = useSelector<RootState, any[]>(
    ({mainReducer}) => mainReducer.userBranches
  ) as any

  if (
    !isTeacher() &&
    !ATTENDANCE_URL_FOR_BRANCH_SELECTION.includes(window.location.pathname) &&
    localStorage.getItem('attendance_branch')
  ) {
    localStorage.removeItem('attendance_branch')
    localStorage.removeItem('branch')
    window.location.reload()
  } else if (
    !isTeacher() &&
    userBranches?.length !== 0 &&
    ATTENDANCE_URL_FOR_BRANCH_SELECTION.includes(window.location.pathname) &&
    !localStorage.getItem('branch')
  ) {
    localStorage.setItem('attendance_branch', `${permittedBranches[0]?.id}`)
    localStorage.setItem('branch', `${permittedBranches[0]?.id}`)
    window.location.reload()
  }

  useEffect(() => {
    if (user.id) {
      posthog?.group('tenant', tenant)
      posthog?.identify(`${user.id}`, {
        ...user,
        userType,
        tenant,
      })
      identifyClarityUser(user.id, tenant, userType)
    }
  }, [posthog, tenant, user, userType])

  return (
    <Routes>
      {/* Onboarding: full-screen, no header/sidebar - outside MasterLayout */}
      {/* Note: WizardLayout mounts only when onboarding is NOT completed, to avoid layout side effects. */}
      <Route
        path='onboarding'
        element={
          <SuspensedView>
            <OnboardingWizardRouteGuard />
          </SuspensedView>
        }
      />
      {/* Per-section deep links (e.g. /onboarding/curriculum/levels) so the backend can
          navigate a user straight to a specific onboarding step + sub-step. */}
      <Route
        path='onboarding/:step'
        element={
          <SuspensedView>
            <OnboardingWizardRouteGuard />
          </SuspensedView>
        }
      />
      <Route
        path='onboarding/:step/:subStep'
        element={
          <SuspensedView>
            <OnboardingWizardRouteGuard />
          </SuspensedView>
        }
      />
      {/* An applicant's admission form, opened by staff from the New Admissions
          list in a new tab. Full-screen like the applicant sees it - the form
          brings its own header and theme root - so it sits outside MasterLayout
          alongside onboarding. */}
      <Route
        path='institute/admission/form/:userId'
        element={
          <SuspensedView>
            <ApplicantFormPage />
          </SuspensedView>
        }
      />
      {/* The print tab PHP's voucher print buttons open for a school printing through Node:
          it becomes the printed page itself, like PHP's tab, so it sits outside MasterLayout. */}
      <Route
        path='institute/print-vouchers/*'
        element={
          <SuspensedView>
            <FeeVoucherPrintPage />
          </SuspensedView>
        }
      />
      {/* Learning Assistant: full-screen with its own sidebar and top bar (AssistantLayout),
          so it sits outside MasterLayout like onboarding. */}
      {isCurriculumAssistantRouteOn() && (
        <Route
          path='learning-assistant/*'
          element={
            <SuspensedView>
              <CurriculumAssistantPage />
            </SuspensedView>
          }
        />
      )}
      <Route element={<SelectiveAiOnboardingLayoutGuard />}>
        {/* Above the layout, so a voucher run goes on while the user moves between pages. */}
        <Route
          element={
            <GenerateVoucherRunDriver>
              <Layout />
            </GenerateVoucherRunDriver>
          }
        >
          {/* Redirect to Dashboard after success login/registration */}
          <Route path='auth/*' element={<Navigate replace to='/dashboard' />} />

          {/* Pages */}
          <Route path='dashboard' element={<Dashboard />} />

          {isDesignToolEnabled() && (
            <>
              <Route
                path='institute/design-tool'
                element={
                  <SuspensedView>
                    <DesignerTool />
                  </SuspensedView>
                }
              />

              <Route
                path='institute/design-tool/versions-history'
                element={
                  <SuspensedView>
                    <VersionsHistory />
                  </SuspensedView>
                }
              />

              <Route
                path='design-tool'
                element={<NavigateWithSearch to='/institute/design-tool' replace />}
              />
              <Route
                path='design-tool/versions-history'
                element={
                  <NavigateWithSearch to='/institute/design-tool/versions-history' replace />
                }
              />
            </>
          )}

          {/* Gated on the permission the API's own route guard asks for, so
              the route and the endpoints behind it can never disagree about
              whether messaging is on. Nothing is deleted when it is off — the
              route comes back with the permission. */}
          {hasPermission('message_manager_view') && (
            <Route path='messages/*' element={<ChatPage />} />
          )}

          <Route
            path='profile/*'
            element={
              <SuspensedView>
                <ProfilePage />
              </SuspensedView>
            }
          />

          {kenzerSsoEnabled && (
            <Route
              path='integrations/kenzer/launch'
              element={
                <SuspensedView>
                  <KenzerLaunchPage />
                </SuspensedView>
              }
            />
          )}

          <Route
            path='user-manual'
            element={
              <SuspensedView>
                <UserManualEntry />
              </SuspensedView>
            }
          />
          <Route
            path='user-manual/:topic'
            element={
              <SuspensedView>
                <UserManualEntry />
              </SuspensedView>
            }
          />

          <Route path='institute'>
            {isDevEnv() && (
              <Route
                path='accountType/*'
                element={
                  <SuspensedView>
                    <AccountTypePage />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='attendance/biometricAttendanceUpload/*'
              element={
                <SuspensedView>
                  <AttendanceUploadPage />
                </SuspensedView>
              }
            />

            <>
              <Route
                path='attendance/setting/*'
                element={
                  <SuspensedView>
                    <AttendanceSettingPage />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance/*'
                element={
                  <SuspensedView>
                    <AttendancePage />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance/teacher-report/*'
                element={
                  <SuspensedView>
                    <TeacherStaffAttendanceReportPage />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance/teacher-subject-report/*'
                element={
                  <SuspensedView>
                    <TeacherSubjectAttendanceReportPage />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance/student-report/*'
                element={
                  <SuspensedView>
                    <StudentAttendanceReport />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance/student-subject-report/*'
                element={
                  <SuspensedView>
                    <StudentSubjectAttendanceReport />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance-custom-schedule/*'
                element={
                  <SuspensedView>
                    <UserCustomAttendanceSchedulePage />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance-remarks/*'
                element={
                  <SuspensedView>
                    <AttendanceRemarksPage />
                  </SuspensedView>
                }
              />
            </>

            <Route
              path='attendance/student-subject/combined-report/'
              element={
                <SuspensedView>
                  <StudentSubjectCombinedAttendanceReport />
                </SuspensedView>
              }
            />

            <Route
              path='admission/*'
              element={
                <SuspensedView>
                  <AdmissionFormPage />
                </SuspensedView>
              }
            />

            <Route
              path='biometricCsvLog/*'
              element={
                <SuspensedView>
                  <BiometricCsvLog />
                </SuspensedView>
              }
            />

            <Route
              path='biometricDeviceLog/*'
              element={
                <SuspensedView>
                  <BiometricDeviceLogPage />
                </SuspensedView>
              }
            />

            <Route
              path='biometricLog/*'
              element={
                <SuspensedView>
                  <BiometricLogPage />
                </SuspensedView>
              }
            />

            <Route
              path='branches/*'
              element={
                <SuspensedView>
                  <BranchPage />
                </SuspensedView>
              }
            />

            <Route
              path='branch-report'
              element={
                <SuspensedView>
                  <BranchUtilizationReport />
                </SuspensedView>
              }
            />

            <Route
              path='certificates/*'
              element={
                <SuspensedView>
                  <CertificatePage />
                </SuspensedView>
              }
            />

            <Route
              path='classes/*'
              element={
                <SuspensedView>
                  <ClassPage />
                </SuspensedView>
              }
            />

            {isAdmin() && (
              <Route
                path='client-secrets/*'
                element={
                  <SuspensedView>
                    <ClientSecretsPage />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='contact-support'
              element={
                <SuspensedView>
                  <ContactSupportPage />
                </SuspensedView>
              }
            />

            <Route
              path='custom-names/*'
              element={
                <SuspensedView>
                  <CustomNamesPage />
                </SuspensedView>
              }
            />

            <Route
              path='data-import/tags/*'
              element={
                <SuspensedView>
                  <TagsUploadPage />
                </SuspensedView>
              }
            />

            <Route
              path='data-import/user-labels/*'
              element={
                <SuspensedView>
                  <LabelsUploadPage />
                </SuspensedView>
              }
            />

            <Route
              path='data-import/store-items/*'
              element={
                <SuspensedView>
                  <StoreItemsUploadPage />
                </SuspensedView>
              }
            />

            <Route
              path='data-import/student-homework/*'
              element={
                <SuspensedView>
                  <StudentHomeworkUploadPage />
                </SuspensedView>
              }
            />

            <Route
              path='data-import/student-test/*'
              element={
                <SuspensedView>
                  <StudentTestUploadPage />
                </SuspensedView>
              }
            />

            <Route
              path='data-import/student-exam/*'
              element={
                <SuspensedView>
                  <StudentExamUploadPage />
                </SuspensedView>
              }
            />

            <Route
              path='data-import/class-subject-teacher/*'
              element={
                <SuspensedView>
                  <CstUploadPage />
                </SuspensedView>
              }
            />

            <Route
              path='data-import/student-parent/*'
              element={
                <SuspensedView>
                  <StudentParentUploadPage />
                </SuspensedView>
              }
            />

            <Route
              path='data-import/annual-calendar/*'
              element={
                <SuspensedView>
                  <AnnualCalendarUploadPage />
                </SuspensedView>
              }
            />

            <Route
              path='data-import/csv-import/*'
              element={
                <SuspensedView>
                  <CsvImportPage />
                </SuspensedView>
              }
            />

            {canOpenAiDataImport() && (
              <Route
                path='data-import/ai-import/*'
                element={
                  <SuspensedView>
                    <AiImportPage />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='dBLog/*'
              element={
                <SuspensedView>
                  <DatabaseLogPage />
                </SuspensedView>
              }
            />

            <Route
              path='departments/*'
              element={
                <SuspensedView>
                  <DepartmentPage />
                </SuspensedView>
              }
            />

            <Route
              path='digital-requests/*'
              element={
                <SuspensedView>
                  <DigitalRequestPage />
                </SuspensedView>
              }
            />

            <Route
              path='fileDownloadTest/'
              element={
                <SuspensedView>
                  <FileDownloadPage />
                </SuspensedView>
              }
            />
            <Route
              path='document-types/*'
              element={
                <SuspensedView>
                  <FileTypePage />
                </SuspensedView>
              }
            />
            <Route
              path='general-settings/*'
              element={
                <SuspensedView>
                  <SettingsPage />
                </SuspensedView>
              }
            />

            <Route
              path='grades/*'
              element={
                <SuspensedView>
                  <GradePage />
                </SuspensedView>
              }
            />

            {/* {((isHomeworkBetaEnabled() && isHomeworkBetaToggleEnabled()) ||
            !isNewHomeworkDisabled()) && ( */}
            <Route
              path='homeworks/*'
              element={
                <SuspensedView>
                  <HomeworkPage />
                </SuspensedView>
              }
            />
            {/* )} */}

            <Route
              path='inactive-users/*'
              element={
                <SuspensedView>
                  <InactiveUserPage />
                </SuspensedView>
              }
            />

            {(isDevEnv() || isDemocollege()) && (
              <Route
                path='inventory/*'
                element={
                  <SuspensedView>
                    <InventoryPage />
                  </SuspensedView>
                }
              />
            )}

            {isDevEnv() && (
              <>
                <Route
                  path='inquiry/*'
                  element={
                    <SuspensedView>
                      <InquiryPage />
                    </SuspensedView>
                  }
                />

                <Route
                  path='inquiry/inquiry-setting/*'
                  element={
                    <SuspensedView>
                      <InquirySettingPage />
                    </SuspensedView>
                  }
                />
              </>
            )}

            <Route
              path='lectures-new/*'
              element={
                <SuspensedView>
                  <LecturePage />
                </SuspensedView>
              }
            />

            <Route
              path='level/*'
              element={
                <SuspensedView>
                  <LevelPage />
                </SuspensedView>
              }
            />

            {LibraryPage && <Route path='library/*' element={<LibraryPage />} />}

            <Route
              path='news/*'
              element={
                <SuspensedView>
                  <NewsPage />
                </SuspensedView>
              }
            />

            <Route
              path='monthly-audit-summary/*'
              element={
                <SuspensedView>
                  <MonthlyAuditSummaryPage />
                </SuspensedView>
              }
            />

            <Route
              path='notificationLog/*'
              element={
                <SuspensedView>
                  <NotificationLogPage />
                </SuspensedView>
              }
            />
            <Route
              path='notification-settings/*'
              element={
                <SuspensedView>
                  <NotificationSettingsPage />
                </SuspensedView>
              }
            />

            <Route
              path='email-settings/*'
              element={
                <SuspensedView>
                  <EmailSettingsPage />
                </SuspensedView>
              }
            />

            <Route
              path='system-logs/*'
              element={
                <SuspensedView>
                  <SystemLogPage />
                </SuspensedView>
              }
            />

            <Route
              path='parent/*'
              element={
                <SuspensedView>
                  <ParentPage />
                </SuspensedView>
              }
            />

            <Route
              path='performance-reports/*'
              element={
                <SuspensedView>
                  <PerformanceReportsPage />
                </SuspensedView>
              }
            />

            <Route
              path='resources/*'
              element={
                <SuspensedView>
                  <ResourcePage />
                </SuspensedView>
              }
            />

            <Route
              path='resource-categories/*'
              element={
                <SuspensedView>
                  <ResourceCategoryPage />
                </SuspensedView>
              }
            />

            <Route
              path='resource-archive-folders/*'
              element={
                <SuspensedView>
                  <ResourceArchiveFolderPage />
                </SuspensedView>
              }
            />

            <Route
              path='resource-settings/*'
              element={
                <SuspensedView>
                  <ResourceSettingsPage />
                </SuspensedView>
              }
            />

            <Route
              path='requests/*'
              element={
                <SuspensedView>
                  <RequestPage />
                </SuspensedView>
              }
            />

            <Route
              path='role-module-permissions/*'
              element={
                <SuspensedView>
                  <RoleModulePermissionsPage />
                </SuspensedView>
              }
            />

            <Route
              path='schedule/*'
              element={
                <SuspensedView>
                  <SchedulePage />
                </SuspensedView>
              }
            />

            <Route
              path='sections/*'
              element={
                <SuspensedView>
                  <SectionPage />
                </SuspensedView>
              }
            />

            <Route
              path='smsLog/*'
              element={
                <SuspensedView>
                  <SmsLogPage />
                </SuspensedView>
              }
            />

            <Route
              path='staff/*'
              element={
                <SuspensedView>
                  <StaffPage />
                </SuspensedView>
              }
            />
            <Route
              path='staff-role/*'
              element={
                <SuspensedView>
                  <StaffRolePage />
                </SuspensedView>
              }
            />
            <Route
              path='label/*'
              element={
                <SuspensedView>
                  <LabelsPage />
                </SuspensedView>
              }
            />
            <Route
              path='student/*'
              element={
                <SuspensedView>
                  <StudentPage />
                </SuspensedView>
              }
            />

            <Route
              path='subjects/*'
              element={
                <SuspensedView>
                  <SubjectsPage />
                </SuspensedView>
              }
            />

            {!isTeacher() && hasPermission('class_subject_manager') && (
              <Route
                path='subject-group/*'
                element={
                  <SuspensedView>
                    <SubjectGroupPage />
                  </SuspensedView>
                }
              />
            )}

            {(hasPermission('tickets_manager') || isSelectiveAiDomains()) && (
              <Route
                path='support-tickets/*'
                element={
                  <SuspensedView>
                    <SupportTicketPage />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='tags/*'
              element={
                <SuspensedView>
                  <TagsPage />
                </SuspensedView>
              }
            />

            <Route
              path='tag-categories/*'
              element={
                <SuspensedView>
                  <TagsCategoryPage />
                </SuspensedView>
              }
            />
            <Route
              path='teacher/*'
              element={
                <SuspensedView>
                  <TeacherPage />
                </SuspensedView>
              }
            />
            {isDevEnv() && (
              <Route
                path='user/mcstudent'
                element={
                  <SuspensedView>
                    <AssignedStudentsPage />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='vendors/*'
              element={
                <SuspensedView>
                  <VendorPage />
                </SuspensedView>
              }
            />

            <Route
              path='tests/*'
              element={
                <SuspensedView>
                  <TestPage />
                </SuspensedView>
              }
            />

            <Route
              path='syllabus/*'
              element={
                <SuspensedView>
                  <SyllabusPage />
                </SuspensedView>
              }
            />

            {/* {(isDevEnv() || ['tres'].includes(getTenantName())) && ( */}
            <Route
              path='royalty/*'
              element={
                <SuspensedView>
                  <RoyaltyPage />
                </SuspensedView>
              }
            />
            {/* Always mounted: exam/* also carries screens that are NOT part
                of the new exam manager (Exam Comments, Consolidated Report,
                Grading Key). Gating the whole mount on the new_exam_manager
                flag 404'd them on prod for every tenant with the flag off.
                ExamPage owns the flag: off, it serves only those screens and
                hands every other exam/* URL to the legacy iframe, exactly as
                the catch-all below did. */}
            <Route
              path='exam/*'
              element={
                <SuspensedView>
                  <ExamPage />
                </SuspensedView>
              }
            />

            {/* The exam-module screens outside `exam/*`, behind the setting
                like the rest of the module. Mounted unconditionally they also
                answered the LEGACY URLs: React Router matches
                case-insensitively, so `/institute/examtypeLabels/admin`
                reached the native page for a tenant whose setting says it
                should get the PHP one. */}
            {newExamManagerEnabled && (
              <Route
                path='exam-type/*'
                element={
                  <SuspensedView>
                    <ExamTypePage />
                  </SuspensedView>
                }
              />
            )}

            {newExamManagerEnabled && (
              <Route
                path='examtypelabels/*'
                element={
                  <SuspensedView>
                    <ExamTypeLabelsPage />
                  </SuspensedView>
                }
              />
            )}

            {/* Reached from the Word template manager's Variables List, which
                links to it by its legacy path. Registered as that one path
                rather than `examGrade/*`, so the rest of the examGrade screens
                keep falling through to the iframe. */}
            {newExamManagerEnabled && (
              <Route
                path='examGrade/addExamGradeComments'
                element={
                  <SuspensedView>
                    <GradewiseComments />
                  </SuspensedView>
                }
              />
            )}

            {newExamManagerEnabled && hasPermission('exam_manager_exam_schedules') && (
              <Route
                path='exam-schedule/*'
                element={
                  <SuspensedView>
                    <ExamSchedulePage />
                  </SuspensedView>
                }
              />
            )}

            {/* Always mounted, as exam/* is: the page checks the permission itself, so a
                permission granted after the route table was built still reaches it. */}
            <Route
              path='generate-vouchers/*'
              element={
                <SuspensedView>
                  <GenerateVoucherPage />
                </SuspensedView>
              }
            />

            {hasPermission('fee_manager_admission_vouchers') && (
              <Route
                path='admission-vouchers/*'
                element={
                  <SuspensedView>
                    <AdmissionVoucherPage />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='events/*'
              element={
                <SuspensedView>
                  <EventPage />
                </SuspensedView>
              }
            />
            <Route
              path='sports/*'
              element={
                <SuspensedView>
                  <SportsRecordPage />
                </SuspensedView>
              }
            />
            {isDevEnv() && (
              <Route
                path='sports/*'
                element={
                  <SuspensedView>
                    <SportsRecordPage />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='counselling/*'
              element={
                <SuspensedView>
                  <CounsellingAdminPage />
                </SuspensedView>
              }
            />

            {/* Always mounted: without student_comments_view the page renders its own
                not-enabled card, so the URL never falls through to the legacy iframe
                (the institute catch-all below). */}
            <Route
              path='student-comments'
              element={
                <SuspensedView>
                  <StudentCommentsPage />
                </SuspensedView>
              }
            />

            <Route
              path='forms/*'
              element={
                <SuspensedView>
                  <FormPage />
                </SuspensedView>
              }
            />

            {newExamManagerEnabled && (
              <Route
                path='exam-report-card-word-templates/*'
                element={
                  <SuspensedView>
                    <ExamReportCardWordTemplatePage />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='observations/*'
              element={
                <SuspensedView>
                  <ObservationPage />
                </SuspensedView>
              }
            />

            <Route
              path='questions/*'
              element={
                <SuspensedView>
                  <QuestionPage />
                </SuspensedView>
              }
            />

            <Route
              path='change-log/*'
              element={
                <SuspensedView>
                  <ChangeLogPage />
                </SuspensedView>
              }
            />

            <Route
              path='whatsappLog/*'
              element={
                <SuspensedView>
                  <WhatsappLogPage />
                </SuspensedView>
              }
            />

            {isSelectiveAiDomains() && (
              <Route
                path='user-management/*'
                element={
                  <SuspensedView>
                    <UserManagementPage />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='diagnose/*'
              element={
                <SuspensedView>
                  <DiagnosePage />
                </SuspensedView>
              }
            />

            <Route path='*' element={<SchoolPage />} />
          </Route>

          <Route path='school/*' element={<SchoolToInstitute />} />

          {/* Page Not Found */}
          <Route path='*' element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  )
}

export default SchoolRoutes
